import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { CHECKLIST_SECTIONS } from './checklist.data';
import { ChecklistRigTag, IChecklistSection, RIG_TAG_TO_TYPES, rvTypeToTag } from './checklist.types';
import { readMyRv } from '../my-rv.util';
import type { RvType } from '../listings/mock-listings.data';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

const STATE_KEY = 'cnt-checklist-state';
const STATE_IDB_KEY = 'checklist:state';

export interface IChecklistState {
  /** IDs of items the user has checked off. */
  checkedIds: Set<string>;
  /** MH users who tow a car see Toad-tagged items. */
  towsVehicle: boolean;
  /** Manually-picked rig tag; null means auto-detect from readMyRv. */
  rigOverride: ChecklistRigTag | null;
}

interface IPersistedState {
  checkedIds: string[];
  towsVehicle: boolean;
  rigOverride: ChecklistRigTag | null;
}

function emptyState(): IChecklistState {
  return { checkedIds: new Set(), towsVehicle: false, rigOverride: null };
}

@Injectable({ providedIn: 'root' })
export class ChecklistService {
  private platformId = inject<object>(PLATFORM_ID);

  private readonly store = createIdbKvStore<IPersistedState>({
    legacyKeys: { [STATE_IDB_KEY]: STATE_KEY },
  });

  private readonly _state$ = new BehaviorSubject<IChecklistState>(emptyState());
  readonly state$: Observable<IChecklistState> = this._state$.asObservable();

  readonly sections: IChecklistSection[] = CHECKLIST_SECTIONS;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this._state$.next(this.read());
      // Note: the localStorage-era cross-tab sync via the native `storage`
      // event has no IndexedDB equivalent (would need BroadcastChannel or
      // similar) and isn't replicated here — a visitor with the checklist
      // open in two tabs won't see one tab's checks reflected in the
      // other until reload. Accepted as low-risk for this checklist.
      void this.store.init().then(() => this._state$.next(this.read()));
    }
  }

  get state(): IChecklistState { return this._state$.value; }

  /** Effective rig tag: user's override, else auto-detected from IMyRv. */
  activeRigTag(): ChecklistRigTag | null {
    const s = this._state$.value;
    if (s.rigOverride) return s.rigOverride;
    if (!isPlatformBrowser(this.platformId)) return null;
    const rv = readMyRv(this.platformId);
    return rvTypeToTag((rv.type as RvType | undefined) ?? null);
  }

  toggle(itemId: string): void {
    const s = this._state$.value;
    const next = new Set(s.checkedIds);
    if (next.has(itemId)) next.delete(itemId);
    else next.add(itemId);
    this.commit({ ...s, checkedIds: next });
  }

  resetSection(sectionId: string): void {
    const s = this._state$.value;
    const section = CHECKLIST_SECTIONS.find(sec => sec.id === sectionId);
    if (!section) return;
    const next = new Set(s.checkedIds);
    for (const item of section.items) next.delete(item.id);
    this.commit({ ...s, checkedIds: next });
  }

  resetAll(): void {
    this.commit({ ...this._state$.value, checkedIds: new Set() });
  }

  setRigOverride(tag: ChecklistRigTag | null): void {
    this.commit({ ...this._state$.value, rigOverride: tag });
  }

  setTowsVehicle(tows: boolean): void {
    this.commit({ ...this._state$.value, towsVehicle: tows });
  }

  /** Items in this section that match the current rig filter. */
  visibleItems(sectionId: string): IChecklistSection['items'] {
    const section = CHECKLIST_SECTIONS.find(s => s.id === sectionId);
    if (!section) return [];
    const rig = this.activeRigTag();
    const tows = this._state$.value.towsVehicle;
    return section.items.filter(item => {
      if (item.tags.length === 0) return true;
      // Toad items visible only for MH users who checked "tows a vehicle"
      if (item.tags.includes('Toad')) return rig === 'MH' && tows;
      // Otherwise visible if any tag matches active rig
      if (!rig) return true; // no rig picked → show everything
      return item.tags.includes(rig);
    });
  }

  /** [completed, total] counts for a section under the current filter. */
  sectionProgress(sectionId: string): [number, number] {
    const items = this.visibleItems(sectionId);
    const done = items.filter(i => this._state$.value.checkedIds.has(i.id)).length;
    return [done, items.length];
  }

  /** [completed, total] across every visible section — for the top progress bar. */
  overallProgress(): [number, number] {
    let done = 0, total = 0;
    for (const s of CHECKLIST_SECTIONS) {
      const [d, t] = this.sectionProgress(s.id);
      done += d; total += t;
    }
    return [done, total];
  }

  private commit(next: IChecklistState): void {
    this._state$.next(next);
    if (!isPlatformBrowser(this.platformId)) return;
    const persisted: IPersistedState = {
      checkedIds: [...next.checkedIds],
      towsVehicle: next.towsVehicle,
      rigOverride: next.rigOverride,
    };
    void this.store.write(STATE_IDB_KEY, persisted);
  }

  private read(): IChecklistState {
    if (!isPlatformBrowser(this.platformId)) return emptyState();
    const parsed = this.store.read(STATE_IDB_KEY);
    if (!parsed) return emptyState();
    return {
      checkedIds: new Set(Array.isArray(parsed.checkedIds) ? parsed.checkedIds : []),
      towsVehicle: !!parsed.towsVehicle,
      rigOverride: (parsed.rigOverride as ChecklistRigTag) || null,
    };
  }

  /** Helper for the print button — matches the SSR-safe pattern used
   *  across other components in the workspace. */
  print(): void {
    if (isPlatformBrowser(this.platformId)) window.print();
  }
}

// Export unused tag map so consumers can render the chip labels
export { RIG_TAG_TO_TYPES };
